import { MouseEvent, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Collapse, Tooltip, Typography } from '@mui/material';

import { ReactComponent as UploadIcon } from '@/assets/icons/import-icon.svg';
import { useOperationSectionExpansion } from '@/shared/lib/hooks/useOperationSectionExpansion';
import AppButton from '@/shared/ui/AppButton';
import { AttachmentOperationGroup } from '@/shared/ui/ImagesModalVersioning';

import { FileData } from './FileCard';

export interface OperationSection<F = FileData> {
  group: AttachmentOperationGroup;
  files: F[];
}

interface OperationSectionListProps<F> {
  sections: OperationSection<F>[];
  initialExpandedOperationId?: string;
  hasUploadOption: boolean;
  maxFileSizeMb?: number;
  emptyText?: string;
  uploadLabel?: string;
  onUploadToOperation?: (operationId?: string) => void;
  renderFile: (file: F) => ReactNode;
}

const OperationSectionList = <F extends { id: number }>({
  sections,
  initialExpandedOperationId,
  hasUploadOption,
  maxFileSizeMb,
  emptyText,
  uploadLabel,
  onUploadToOperation,
  renderFile,
}: OperationSectionListProps<F>): ReactElement => {
  const { t } = useTranslation();

  const sectionIdsKey = sections.map((s) => s.group.id).join(',');
  const { expandedIds, toggle } = useOperationSectionExpansion(
    sectionIdsKey,
    initialExpandedOperationId,
  );

  return (
    <>
      {sections.map(({ group, files }) => {
        const expanded = expandedIds.has(group.id);
        return (
          <Box key={group.id} data-testid={`operation-section-${group.id}`}>
            <Box
              data-testid={`operation-section-header-${group.id}`}
              onClick={() => toggle(group.id)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', py: 0.5 }}
            >
              {expanded ? (
                <KeyboardArrowUpIcon sx={{ fontSize: '1.25rem', color: '#504E5A' }} />
              ) : (
                <KeyboardArrowDownIcon sx={{ fontSize: '1.25rem', color: '#504E5A' }} />
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ color: '#504E5A', fontSize: '0.9375rem', fontWeight: 500 }}>
                  {group.title}
                </Typography>
                {group.subtitle && (
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>
                    {group.subtitle}
                  </Typography>
                )}
              </Box>
              {hasUploadOption && (
                <Tooltip
                  arrow
                  placement="top"
                  title={maxFileSizeMb ? t('tooltips.max_file_size', { size: maxFileSizeMb }) : ''}
                  disableHoverListener={!maxFileSizeMb}
                >
                  <span>
                    <AppButton
                      variant="primary"
                      size="small"
                      startIcon={<UploadIcon />}
                      data-testid={`operation-section-upload-${group.id}`}
                      onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        onUploadToOperation?.(group.operationId);
                      }}
                    >
                      {uploadLabel ?? t('operation_group_add_artwork')}
                    </AppButton>
                  </span>
                </Tooltip>
              )}
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 3, pb: 1 }}>
                {files.length === 0 ? (
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.875rem' }}>
                    {emptyText ?? t('operation_group_no_artwork_uploaded')}
                  </Typography>
                ) : (
                  files.map((file) => (
                    <div key={file.id} data-file-id={file.id}>
                      {renderFile(file)}
                    </div>
                  ))
                )}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </>
  );
};

export default OperationSectionList;
